import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useEffect, useState } from 'react';
import { PageLoadingSpinner } from '@/components/ui/loading-spinner';

interface ProtectedRouteProps {
  children: JSX.Element;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { isAuthenticated, isLoading, user, isFirstTimeLogin } = useAuth();
  const location = useLocation();
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
  const [redirectAttempts, setRedirectAttempts] = useState(0);

  useEffect(() => {
    // Log authentication status when the component mounts or updates
    console.log('ProtectedRoute - Auth Status:', {
      isLoading,
      isAuthenticated,
      user: !!user,
      isFirstTimeLogin,
      path: location.pathname,
      hasCheckedAuth,
      redirectAttempts
    });

    // Mark that we've checked authentication status
    if (!isLoading && !hasCheckedAuth) {
      setHasCheckedAuth(true);
    }
  }, [isLoading, isAuthenticated, user, location.pathname, hasCheckedAuth, redirectAttempts]);

  // Always show loading state on first render until auth check completes
  if (isLoading || !hasCheckedAuth) {
    console.log('ProtectedRoute - Still loading authentication status or first check');
    return <PageLoadingSpinner message="Checking authentication..." />;
  }

  // Only redirect if we've completed the auth check and user is not authenticated
  // Also check for redirect loops
  if (!isAuthenticated) {
    console.log('ProtectedRoute - Not authenticated, redirecting to login');

    // Check for redirect loops
    if (redirectAttempts > 3) {
      console.warn('ProtectedRoute - Too many redirect attempts, showing error');

      // Reset redirect attempts
      setRedirectAttempts(0);

      // Force reload the login page
      window.location.href = '/login';

      return <PageLoadingSpinner message="Authentication Error - Redirecting..." />;
    }

    // Increment redirect attempts
    setRedirectAttempts(prev => prev + 1);

    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Check for first-time login after authentication
  if (isFirstTimeLogin) {
    console.log('ProtectedRoute - First-time login detected, redirecting to password reset');
    return <Navigate to="/first-time-password-reset" replace />;
  }

  // Reset redirect attempts when successfully authenticated
  if (redirectAttempts > 0) {
    setRedirectAttempts(0);
  }

  console.log('ProtectedRoute - Authenticated, rendering children');
  return children;
};

export default ProtectedRoute;
